// Point `@monaco-editor/react`'s loader at our bundled monaco instead of
// jsdelivr. Without this, the static `import 'monaco-editor'` lines elsewhere
// bundle one copy while the loader CDN-loads another — two instances with
// independent module state. Must run before any <Editor> mounts.
//
// This file statically imports all of monaco-editor, so importing it from an
// entry graph drags the whole editor bundle into the entry chunk. It is
// imported from three places, which together cover every editor mount:
// webapp's own entry (index.tsx, eager — unchanged behavior for
// codeinplace.stanford.edu), CourseRoutes (the lazily-loaded course subtree —
// how PAI gets it without paying for monaco on its landing page, see
// [#3616](https://github.com/CodeinPlace/cip/issues/3616)), and the
// application-flow code editor (course/application/editor/components/
// CodeEditor.tsx, which mounts outside CourseRoutes). A new editor mounted
// outside those graphs must import this file itself.
import * as monaco from 'monaco-editor';
import EditorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import JsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import TsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';
import {loader} from '@monaco-editor/react';
import {registerPythonGrammar} from './pythonGrammar';

self.MonacoEnvironment = {
  // TypeScript and JavaScript models require the dedicated TS worker, which
  // implements language-service methods like `getDocumentHighlights`,
  // `getSyntacticDiagnostics`, and `getCodeFixesAtPosition`. The generic
  // EditorWorker doesn't register these handlers, so any `.ts`/`.tsx` model
  // would throw "Missing requestHandler or method: ..." on cursor moves and
  // edits. The TS worker ships as its own Vite chunk and is only fetched when
  // a TypeScript or JavaScript model is actually opened.
  // Docs: https://microsoft.github.io/monaco-editor/docs.html#interfaces/Environment.html
  getWorker(_workerId: string, label: string): Worker {
    if (label === 'typescript' || label === 'javascript') {
      return new TsWorker();
    }
    // Schema validation, hovers and completion for JSON live in this worker; the
    // generic EditorWorker below registers none of it.
    if (label === 'json') {
      return new JsonWorker();
    }
    return new EditorWorker();
  },
};

registerPythonGrammar(monaco);

loader.config({monaco});

// Monaco switches to its EditContext input surface whenever the browser
// exposes the API, but that surface only supports Chromium: under Firefox's
// EditContext, keystrokes are dropped or duplicated and Backspace inserts
// text. The substring test mirrors Monaco's own isChrome, which Edge matches.
// Registering the listener initializes Monaco's services eagerly, so skip it
// where there is no EditContext for Monaco to pick up.
if ('EditContext' in globalThis && !navigator.userAgent.includes('Chrome')) {
  monaco.editor.onDidCreateEditor(editor => {
    editor.updateOptions({editContext: false});
  });
}
